import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * "The Language of Change: Mastering Communication for Transformation Success" —
 * the e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces, sent as four
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the armoured-parrot artwork beside it, the grey "Why
 * Download?" card with the pair of cover mockups and its four bullets, the
 * "What You'll Learn" card with the stack of printed copies and its six, and the
 * centred "Ready to Turn Communication Into Your Superpower?" ask. The copy is
 * that page's copy, verbatim.
 *
 * Two places where the reference and this site disagree, resolved the way the
 * sibling e-book pages resolve them:
 *
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    was asked for in the same message (*"en aşağıda testimonial template ini
 *    kullanalım"*). The card is Akçansa's: of the twenty in
 *    `content/testimonials.ts` it is the one about **aligning cross-functional
 *    teams and sparking company-wide awareness**, which is this book's subject,
 *    and it carries both a company mark and a photograph of the person speaking.
 *    Its words are the Hall of Fame's; that file is where a change to them
 *    belongs.
 *
 * **The slug is the reference's, not the title rule's.** The screenshots came
 * from `digitopia.co/impact-labs/ebooks/mastering-communication-for-transformation/`
 * — the filenames carry the address — so that is this page's address, and the
 * catalogue entry now points its card here.
 */
export const route: RouteDef = {
  id: 'ebookMasteringCommunication',
  path: 'impact-labs/ebooks/mastering-communication-for-transformation',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket — the same object the e-book
 * catalogue already points its card at. A local constant rather than a key in
 * `content/pages/links.ts`, because a page's own download is nobody else's and
 * five concurrent runs appending to that file is a push conflict.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '50164e94-2178-4740-a318-0e941e4174ce-Mastering%20Communication%20for%20Transformation%20Success.pdf'

export const ebookMasteringCommunication: WrittenPage = {
  navLabel: 'Mastering Communication for Transformation Success',
  title: 'Mastering Communication for Transformation Success',
  description:
    'The Language of Change: how to align, inspire and mobilize your people with clarity and '
    + 'purpose at every stage of change. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'The Language of Change:',
      headline: 'Mastering Communication for Transformation Success',
      body: [
        'Transformation fails without communication. Discover how to align, inspire, and '
          + 'mobilize your people with clarity and purpose at every stage of change.',
      ],
      ctas: [{ label: 'Download', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a parrot in blue mechanical armour with a red crest, '
          + 'against streaks of orange and teal.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the parrot's crest off the top of it.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. It is generated art '
        + 'rather than a photograph, so a bigger export should exist wherever it was '
        + 'made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/language-of-change-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download?',
      body: [
        'Communication is often overlooked in transformation, yet it is the difference between '
          + 'alignment and chaos, momentum and stagnation. This Ebook is your practical guide to:',
      ],
      points: [
        'Engaging employees with clarity and empathy',
        'Creating narratives that unite stakeholders',
        'Avoiding the most common communication pitfalls',
        'Driving real transformation, powered by storytelling, trust, and strategic messaging',
      ],
      outro: [
        'Whether you’re leading a digital, cultural, operational, or sustainability '
          + 'transformation, this resource will help you communicate change like it '
          + 'matters, because it does.',
      ],
      ctas: [{ label: 'Download the Ebook', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-covers.webp',
        alt:
          'Two copies of Mastering Communication for Transformation Success, one behind the '
          + 'other, on a pale gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'Proven frameworks like the 4A Model (Awareness, Alignment, Activation, Advocacy) and '
          + 'the 3H Approach (Head, Heart, Hands) to structure internal messaging',
        'How to craft powerful transformation narratives that inspire action across your '
          + 'organization',
        'Best practices and real-world case studies from LEGO, GE, Wolters Kluwer, and more',
        'How creative metaphors and themes can supercharge engagement, from “The Hero’s Journey” '
          + 'to “Voyage to Value”',
        'Tactics to avoid the most common communication mistakes that derail transformation',
        'Metrics to track and improve your communication’s effectiveness',
      ],
      ctas: [{ label: 'Download the Ebook', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-stack.webp',
        alt:
          'A stack of printed copies of Mastering Communication for Transformation Success seen '
          + 'from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Ready to Turn Communication Into Your Superpower?',
      sub: 'Download the free Ebook and discover how the right words can drive real change.',
      cta: { label: 'Download the Ebook', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Akçansa card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'Using Digitopia’s tools has been incredibly valuable for us. DMI and Impact Platform '
            + 'helped us achieve a clear, structured roadmap for digital transformation. By '
            + 'engaging cross-functional teams from HR, sales, and IT, we aligned on priorities, '
            + 'sparked company-wide awareness, and identified key projects to drive change '
            + 'effectively.',
          source: 'Aydın Şener, Digital Transformation and Industry 4.0 Manager',
          company: 'Akçansa',
          logo: { name: 'Akçansa', src: '/logo/akcansacard.png' },
          portrait: { src: '/photo/portrait-aydin-sener.png', alt: 'Aydın Şener' },
        },
      ],
    },
  ],
}
